import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { config } from '../../utils/config';
import './MobileAccountCard.css';

interface MobileAccountCardProps {
  compact?: boolean;
}

interface AccountData {
  credits_balance: number;
}

const MobileAccountCard: React.FC<MobileAccountCardProps> = ({
  compact = false
}) => {
  const navigate = useNavigate();
  const { user, session } = useAuth();
  const [accountData, setAccountData] = useState<AccountData | null>(null);
  const [loading, setLoading] = useState(true);

  // Fetch account data
  const loadAccountData = async () => {
    if (!user || !session) {
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${config.API_URL}/api/payments-simple/account`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load account data');
      }

      const data = await response.json();
      setAccountData(data.account);
    } catch (err: any) {
      console.error('Error loading account data:', err);
      // Don't show error, just use fallback data
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAccountData();
  }, [user, session]);

  const getPlanName = (): string => {
    return 'Credit-Based Plan';
  };

  if (!user) {
    return (
      <div className={`mobile-account-card ${compact ? 'compact' : ''}`}>
        <div className="mobile-account-signed-out">
          <div className="mobile-account-avatar-empty">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" fill="currentColor"/>
            </svg>
          </div>
          <div className="mobile-account-info">
            <h3 className="mobile-account-name">Guest User</h3>
            <p className="mobile-account-status">Not signed in</p>
          </div>
        </div>
      </div>
    );
  }

  const getUserInitials = (email: string): string => {
    if (!email) return 'U';
    const parts = email.split('@')[0].split('.');
    if (parts.length >= 2) {
      return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
    }
    return email.charAt(0).toUpperCase();
  };

  const formatEmail = (email: string): string => {
    // Extract just the username part (before @)
    const local = email.split('@')[0];
    return local;
  };

  return (
    <div className={`mobile-account-card ${compact ? 'compact' : ''}`}>
      <div className="mobile-account-header">
        <div className="mobile-account-user">
          <div className="mobile-account-avatar">
            {getUserInitials(user.email || '')}
          </div>
          <div className="mobile-account-info">
            <h3 className="mobile-account-name">
              {formatEmail(user.email || 'User')}
            </h3>
            <p className="mobile-account-status">
              {loading ? 'Loading...' : (
                `${getPlanName()} • ${accountData?.credits_balance || 0} credits`
              )}
            </p>
          </div>
        </div>
        
        {!compact && (
          <button 
            className="mobile-account-settings"
            onClick={() => navigate('/account/settings')}
            aria-label="Account settings"
          >
            Settings
          </button>
        )}
      </div>


      {!compact && (
        <div className="mobile-account-actions">
          <button 
            className="mobile-account-action secondary"
            onClick={() => navigate('/account/reports')}
          >
            My Reports
          </button>
          <button 
            className="mobile-account-action secondary"
            onClick={() => navigate('/account/overview')}
          >
            Account Overview
          </button>
        </div>
      )}
    </div>
  );
};

export default MobileAccountCard; 